import { withAuth } from "@salespark/api-client";
import uniqid from "uniqid";
import { isOnline } from "utils/networkConnection";
const VERSION = 12;
const CACHE_PREFIX = "md-cache";
const API_CACHE = `md-v${VERSION}`;
const cache = await caches.open(API_CACHE);

const validateQueuedAction = (action) => {
  if (!action || typeof action !== "object") {
    return { status: false, data: "Invalid offline action payload" };
  }
  if (!action?.method) {
    return { status: false, data: "Offline action method is required" };
  }
  if (!action?.url) {
    return { status: false, data: "Offline action url is required" };
  }
  if (!action?.id) {
    return { status: false, data: "Offline action id is required" };
  }

  const queuedBy = action?.queued_by || {};
  const queuedByUserId = queuedBy?.user_id || queuedBy?._id || queuedBy?.id || null;
  if (!queuedByUserId) {
    return { status: false, data: "Offline action staff ID is required" };
  }
  if (!queuedBy?.token) {
    return { status: false, data: "Offline action staff token is required" };
  }

  return { status: true, data: "OK" };
};

/******************************************************************
 * ##: Build an offline-first API client with IndexedDB cache and action queue
 * Wrap @salespark/api-client withAuth calls to cache reads offline and queue writes for later synchronization.
 *
 * TL;DR: Read from cache when offline and queue mutations to sync later.
 * Uses IndexedDB for tables and an "actions" store for POST/PUT operations.
 * @param {any} authData - Auth payload used to set x-user/x-boats-token headers (type inferred, not enforced here)
 * @returns {object} - API wrapper exposing get/getOne/getMany/post/put/delete methods
 * History:
 * 12-02-2026: Created
 ******************************************************************/
export const offlineHandler = (authData) => {
  const apiInfo = {
    authHeaders: {
      "x-user": authData?._id,
      "x-boats-token": authData?.token,
    },
    baseURL: process.env.REACT_APP_API_URL + process.env.REACT_APP_MOBILE_URL,
  };

  const withAuthPointer = withAuth({ ...apiInfo });

  return {
    //GET OFFLINE -----------------------------------------------------
    getOffline: async (url) => {
      const response = await getTableDataFromCache(url);
      return response;
    },

    // GET ------------------------------------------------------------
    get: async (url) => {
      if (isOnline()) {
        const response = await withAuthPointer.get(url);
        if (response.status) {
          saveTablesToCache(url, response.data);
        }
        return response;
      }
      return await getTableDataFromCache(url);
    },

    // GET ONE ------------------------------------------------------------
    getOne: async (url) => {
      if (isOnline()) {
        const response = await withAuthPointer.getOne(url);
        if (response.status) {
          saveTablesToCache(url, response.data);
        }
        return response;
      }
      return await getTableDataFromCache(url);
    },

    // GET MANY ------------------------------------------------------------
    getMany: async (url, params) => {
      if (isOnline()) {
        const response = await withAuthPointer.getMany(url, params);
        if (response.status) {
          saveTablesToCache(url, response.data);
        }
        return response;
      }
      return await getTableDataFromCache(url);
    },

    // POST ------------------------------------------------------------
    post: async (url, data) => {
      try {
        if (isOnline()) {
          return withAuthPointer.post(url, data);
        }
        console.log("Saving POST action for offline sync (IndexedDB)");
        const queuedAction = {
          method: "POST",
          url: url,
          data: data,
          id: uniqid("post-"),
          queued_by: {
            user_id: authData?._id,
            token: authData?.token,
            name: authData?.name,
          },
          queued_at: new Date().toISOString(),
        };

        const queuedActionValidationResponse = validateQueuedAction(queuedAction);
        if (queuedActionValidationResponse.status === false) {
          return queuedActionValidationResponse;
        }

        const saveQueuedActionResponse = await saveActionToStore(queuedAction);
        if (saveQueuedActionResponse.status === false) {
          return { status: false, data: "Could not save offline action for synchronization" };
        }
      } catch (error) {
        console.error("offlineHandler post cache error:", error);
        return { status: false, data: "Critical error while saving offline action" };
      }
      return { status: true, data: data };
    },

    // PUT ------------------------------------------------------------
    put: async (url, data) => {
      try {
        if (isOnline()) {
          return withAuthPointer.put(url, data);
        }
        console.log("Saving PUT action for offline sync (IndexedDB)");
        const queuedAction = {
          method: "PUT",
          url: url,
          data: data,
          id: uniqid("put-"),
          queued_by: {
            user_id: authData?._id,
            token: authData?.token,
            name: authData?.name,
          },
          queued_at: new Date().toISOString(),
        };

        const queuedActionValidationResponse = validateQueuedAction(queuedAction);
        if (queuedActionValidationResponse.status === false) {
          return queuedActionValidationResponse;
        }

        const saveQueuedActionResponse = await saveActionToStore(queuedAction);
        if (saveQueuedActionResponse.status === false) {
          return { status: false, data: "Could not save offline action for synchronization" };
        }
      } catch (error) {
        console.error("offlineHandler post cache error:", error);
        return { status: false, data: "Critical error while saving offline action" };
      }
      return { status: true, data: data };
    },
    delete: async (url) => {
      // return withAuthPointer.delete(props);
    },
  };
};

/******************************************************************
 * ##: Build a sync GET client that stores results in the Cache Storage API
 * Execute authenticated GET requests and persist successful payloads into Cache Storage keyed by URL and options.
 *
 * TL;DR: Fetch online data and cache it for faster repeat reads.
 * Returns a small client exposing a cached get() method.
 * @param {any} authData - Auth payload used to set x-email/x-boats-token headers (type inferred, not enforced here)
 * @returns {Promise<object>} - Resolves an object with get(url, options) or a failure contract on setup error
 * History:
 * 12-02-2026: Created
 ******************************************************************/
export const syncHandler = (authData) => {
  try {
    const apiInfo = {
      authHeaders: {
        "x-email": authData?.email,
        "x-boats-token": authData?.token,
      },
      baseURL: process.env.REACT_APP_API_URL,
    };
    return {
      get: async (url, options) => {
        try {
          const withAuthPointer = withAuth({ ...apiInfo });
          const key = new Request(apiInfo.baseURL + url + JSON.stringify(options));
          const response = await withAuthPointer.get(url, options);
          if (!response.status) {
            return { status: false, data: response.data };
          }

          cache.put(key, new Response(JSON.stringify(response.data), { headers: { "Content-Type": "application/json" } }));

          return { status: true, data: response.data };

          // Error handling
        } catch (error) {
          return { status: false, data: error };
        }
      },
    };

    // Error handling
  } catch (error) {
    return { status: false, data: error };
  }
};

/******************************************************************
 * ##: Read all cached records from an IndexedDB object store
 * Fetch all rows from the provided IndexedDB store and always resolve a SalesParkContract result.
 *
 * TL;DR: Load table-like data from IndexedDB for offline reads.
 * Always resolves a success/failure contract and guards non-browser usage.
 * @param {string} table - IndexedDB object store name to read from
 * @returns {Promise<SalesParkContract<object>>} - Return a SalesPark Contract object (with Promise).
 * History:
 * 12-02-2026: Created
 ******************************************************************/
export const getTableDataFromCache = async (table) => {
  // Guard against non-browser environments
  if (typeof indexedDB === "undefined") {
    return { status: false, data: "IndexedDB not available" };
  }
  return new Promise(async (resolve) => {
    try {
      const db = await openDatabase();
      const transaction = db.transaction(table, "readonly");
      const store = transaction.objectStore(table);
      const request = store.getAll();

      request.onsuccess = () => {
        resolve({ status: true, data: request.result });
        db.close();
        return;
      };
      request.onerror = () => {
        resolve({ status: false, data: request.error });
        db.close();
        return;
      };

      // Error handling
    } catch (error) {
      resolve({ status: false, data: error });
    }
  });
};

/******************************************************************
 * ##: Save table data into IndexedDB cache
 * Validate and normalize items, then replace the cached content of the target IndexedDB store for offline reads.
 *
 * TL;DR: Cache a batch of records into IndexedDB for offline/fast reads.
 * Validates inputs, guards non-browser usage, then writes into the provided store.
 * @param {string} table - IndexedDB object store name to save data into
 * @param {Array<object>} data - Items to cache; each item must include an "_id" field used to create a string "id"
 * @returns {Promise<SalesParkContract<object>>} - Return a SalesPark Contract object
 * History:
 * 29-01-2026: Created
 * 12-02-2026: Updated
 ******************************************************************/
export const saveTablesToCache = async (table, data) => {
  try {
    // Validation
    if (!Array.isArray(data)) {
      return { status: false, data: "Data must be an array" };
    }

    if (data.length === 0) {
      return { status: true, data: "OK" }; // Nothing to save, early return
    }

    // Validate that all items have an 'id' field
    const invalidItems = data.filter((item) => !item || item._id === undefined);
    if (invalidItems.length > 0) {
      return { status: false, data: `All items must have an '_id' field. Found ${invalidItems.length} invalid item(s)` };
    }

    // Normalize data: ensure all items have 'id' field (use _id if id is missing)
    const normalizedData = data.map((item, index) => {
      if (!item) {
        return { status: false, data: `Item at index ${index} is null or undefined` };
      }

      // Check if item has either 'id' or '_id'
      if (item.id === undefined && item._id === undefined) {
        return { status: false, data: `Item at index ${index} missing 'id' or '_id'` };
      }

      // Normalize: create a copy with 'id' field
      return {
        ...item,
        id: item._id.toString(), // Ensure _id is a string
      };
    });

    // Guard against non-browser environments
    if (typeof indexedDB === "undefined") {
      return { status: false, data: "IndexedDB not available" };
    }

    try {
      const db = await openDatabase();
      await saveToStore(db, table, normalizedData);
      db.close();
      return { status: true, data: "OK" };

      // Error handling
    } catch (error) {
      return { status: false, data: error };
    }

    // Error handling
  } catch (error) {
    console.error("saveStocksToCache error:", error);
    return { status: false, data: error };
  }
};

/******************************************************************
 * ##: Open the IndexedDB cache database
 * Open a versioned IndexedDB database and ensure required object stores exist to support later read/write operations.
 * Resolves with the database instance ready to be used in a transaction.
 *
 * TL;DR: Open (and upgrade if needed) the cache database used for offline persistence.
 * Ensures required stores exist, then returns the IDBDatabase instance.
 * @returns {Promise<IDBDatabase>} - Resolves with an open IDBDatabase instance ready for transactions
 * History:
 * 29-01-2026: Created
 ******************************************************************/
const openDatabase = () => {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(CACHE_PREFIX, VERSION);

    request.onupgradeneeded = (event) => {
      const db = event.target.result;

      if (!db.objectStoreNames.contains("/goods/stocks")) {
        db.createObjectStore("/goods/stocks", { keyPath: "id" });
      }
      if (!db.objectStoreNames.contains("/goods/wares")) {
        db.createObjectStore("/goods/wares", { keyPath: "id" });
      }
      if (!db.objectStoreNames.contains("/equipments")) {
        db.createObjectStore("/equipments", { keyPath: "id" });
      }
      if (!db.objectStoreNames.contains("/staff")) {
        db.createObjectStore("/staff", { keyPath: "id" });
      }
      if (!db.objectStoreNames.contains("/staff/occupations")) {
        db.createObjectStore("/staff/occupations", { keyPath: "id" });
      }
      if (!db.objectStoreNames.contains("/staff/crew")) {
        db.createObjectStore("/staff/crew", { keyPath: "id" });
      }
      if (!db.objectStoreNames.contains("/staff/list")) {
        db.createObjectStore("/staff/list", { keyPath: "id" });
      }
      if (!db.objectStoreNames.contains("/services")) {
        db.createObjectStore("/services", { keyPath: "id" });
      }
      if (!db.objectStoreNames.contains("/services/by-day")) {
        db.createObjectStore("/services/by-day", { keyPath: "id" });
      }
      if (!db.objectStoreNames.contains("/entry/occupancy")) {
        db.createObjectStore("/entry/occupancy", { keyPath: "id" });
      }
      if (!db.objectStoreNames.contains("/goods/sales")) {
        db.createObjectStore("/goods/sales", { keyPath: "id" });
      }
      if (!db.objectStoreNames.contains("/assets/staff-pictures")) {
        db.createObjectStore("/assets/staff-pictures", { keyPath: "staff_id" });
      }

      if (!db.objectStoreNames.contains("actions")) {
        db.createObjectStore("actions", { keyPath: "id" });
      }
      if (!db.objectStoreNames.contains("movements")) {
        db.createObjectStore("movements", { keyPath: "id" });
      }
      if (!db.objectStoreNames.contains("stocksToUpdate")) {
        db.createObjectStore("stocksToUpdate", { keyPath: "id" });
      }
      if (!db.objectStoreNames.contains("countedStocks")) {
        db.createObjectStore("countedStocks", { keyPath: "id" });
      }
      if (!db.objectStoreNames.contains("selectedServiceByStaff")) {
        db.createObjectStore("selectedServiceByStaff", { keyPath: "id" });
      }

      // Offline GPS location records tied to boat services (v12).
      // NOTE: synced_at is not indexed on purpose; IndexedDB does not index null keys,
      // and unsynced records carry synced_at === null. Unsynced draining uses getAll()+filter,
      // consistent with the rest of this module. The indexes below are non-null lookups.
      if (!db.objectStoreNames.contains("locationRecords")) {
        const locationStore = db.createObjectStore("locationRecords", { keyPath: "id" });
        locationStore.createIndex("service_id_raw", "service_id_raw", { unique: false });
        locationStore.createIndex("recorded_at", "recorded_at", { unique: false });
        locationStore.createIndex("action_reference", "action_reference", { unique: false });
      }
    };

    request.onerror = () => {
      reject(new Error(`Failed to open database: ${request.error?.message || "Unknown error"}`));
    };

    request.onsuccess = () => {
      resolve(request.result);
    };
  });
};

/******************************************************************
 * ##: Save an offline action into the IndexedDB "actions" queue
 * Store a pending mutation request (method, url, payload, id) in IndexedDB so it can be replayed during sync.
 *
 * TL;DR: Queue offline POST/PUT requests for later synchronization.
 * Writes an action record into the "actions" object store by id.
 * @param {object} data - Action payload containing method, url, data, and an id (type inferred, not enforced here)
 * @returns {Promise<SalesParkContract<object>>} - Return a SalesPark Contract object (with Promise).
 * History:
 * 12-02-2026: Updated
 ******************************************************************/
const saveToStore = (db, table, data) => {
  return new Promise((resolve, reject) => {
    try {
      const transaction = db.transaction(table, "readwrite");
      const store = transaction.objectStore(table);
      // Clear previous cache
      store.clear();

      // Batch insert all items
      for (const item of data) {
        store.put(item);
      }

      transaction.oncomplete = () => {
        resolve();
      };

      transaction.onerror = () => {
        reject(new Error(`Transaction failed: ${transaction.error?.message || "Unknown error"}`));
      };

      // Error handling
    } catch (error) {
      console.log("saveToStore error:", error);
      reject(error);
    }
  });
};

/******************************************************************
 * ##: Save an offline action into the IndexedDB "actions" queue
 * Store a pending mutation request (method, url, payload, id) in IndexedDB so it can be replayed during sync.
 *
 * TL;DR: Queue offline POST/PUT requests for later synchronization.
 * Writes an action record into the "actions" object store by id.
 * @param {object} data - Action payload containing method, url, data, and an id (type inferred, not enforced here)
 * @returns {Promise<SalesParkContract<object>>} - Return a SalesPark Contract object
 * History:
 * 12-02-2026: Updated
 ******************************************************************/
export const saveActionToStore = (data) => {
  return new Promise(async (resolve) => {
    try {
      const queuedActionValidationResponse = validateQueuedAction(data);
      if (queuedActionValidationResponse.status === false) {
        resolve(queuedActionValidationResponse);
        return;
      }

      // Save to IndexedDB - endpoint actions (post and put requests)
      const db = await openDatabase();
      const transaction = db.transaction("actions", "readwrite");
      const store = transaction.objectStore("actions");

      store.put(data);

      transaction.oncomplete = () => {
        db.close();
        resolve({ status: true, data: "OK" });
      };
      transaction.onerror = () => {
        db.close();
        resolve({ status: false, data: transaction.error });
      };

      // Error handling
    } catch (error) {
      console.error("saveActionsToStore error:", error);
      resolve({ status: false, data: error });
    }
  });
};

/******************************************************************
 * ##: saveMovementsToStore - Persist movement data into IndexedDB
 * Saves a movement record into the "movements" object store using IndexedDB.
 * Wraps the transaction lifecycle in a Promise and resolves a SalesParkContract-like response.
 *
 * TL;DR: Stores movement data locally in IndexedDB and returns a status-based contract.
 * Ensures DB is closed after transaction completion or failure.
 * @param {object} data - Movement object to be persisted in the IndexedDB store
 * @returns {Promise<SalesParkContract<object>>} - Return a SalesPark Contract object (with Promise).
 * History:
 * 13-02-2026: Created
 ******************************************************************/
export const saveMovementsToStore = (data) => {
  return new Promise(async (resolve) => {
    try {
      const db = await openDatabase();

      const transaction = db.transaction("movements", "readwrite");
      const store = transaction.objectStore("movements");
      store.put(data);

      transaction.oncomplete = () => {
        db.close();
        resolve({ status: true, data: "OK" });
      };

      transaction.onerror = () => {
        db.close();
        resolve({ status: false, data: transaction.error });
      };
    } catch (error) {
      console.error("saveMovementsToStore error:", error);
      resolve({ status: false, data: error });
    }
  });
};

/******************************************************************
 * ##: Read all queued offline actions from the IndexedDB "actions" store
 * Open the cache database and fetch every queued action so the sync process can replay pending mutations in order.
 *
 * TL;DR: Load the offline action queue from IndexedDB.
 * Returns all actions or a failure contract if read fails.
 * @returns {Promise<SalesParkContract<object>>} - Return a SalesPark Contract object
 * History:
 * 12-02-2026: Updated
 ******************************************************************/
export const getActionsFromStore = async () => {
  try {
    // Get all pending actions from IndexedDB
    const db = await openDatabase();
    const transaction = db.transaction("actions", "readonly");
    const store = transaction.objectStore("actions");
    const request = store.getAll();

    return new Promise((resolve) => {
      request.onsuccess = () => {
        resolve({ status: true, data: request.result });
        db.close();
      };

      request.onerror = () => {
        resolve({ status: false, data: request.error });
        db.close();
      };
    });

    // Error handling
  } catch (error) {
    console.error("getActionsFromStore error:", error);
    return { status: false, data: error };
  }
};

/******************************************************************
 * ##: getMovementsFromStore - Retrieve all movement records from IndexedDB
 * Fetches all records stored in the "movements" object store using a readonly transaction.
 * Wraps the IndexedDB request lifecycle in a Promise and resolves a SalesParkContract response.
 *
 * TL;DR: Retrieves all locally stored movements from IndexedDB.
 * Returns a SalesPark-style status contract with the stored records or error.
 * @returns {Promise<SalesParkContract<object[]>>} - Return a SalesPark Contract object (with Promise).
 * History:
 * 13-02-2026: Created
 ******************************************************************/

export const getMovementsFromStore = async () => {
  try {
    // Get movements done in sales/consumptions from IndexedDB
    const db = await openDatabase();
    const transaction = db.transaction("movements", "readonly");
    const store = transaction.objectStore("movements");
    const request = store.getAll();

    return new Promise((resolve) => {
      request.onsuccess = () => {
        resolve({ status: true, data: request.result });
        db.close();
      };

      request.onerror = () => {
        resolve({ status: false, data: request.error });
        db.close();
      };
    });

    // Error handling
  } catch (error) {
    console.error("getMovementsFromStore error:", error);
    return { status: false, data: error };
  }
};

const isSameDay = (leftDate, rightDate) => {
  try {
    const left = new Date(leftDate);
    const right = new Date(rightDate);
    if (Number.isNaN(left.getTime()) || Number.isNaN(right.getTime())) {
      return false;
    }

    return left.getFullYear() === right.getFullYear() && left.getMonth() === right.getMonth() && left.getDate() === right.getDate();
  } catch (error) {
    return false;
  }
};

/******************************************************************
 * ##: getMovementsFromStoreByContext - Retrieve movement draft by staff/service/equipment context
 * Retrieves movement records from IndexedDB and returns the latest record matching the provided context.
 * Context matching is strict for user and service and optional for equipment/date.
 * @param {object} context - Context used to filter movements
 * @returns {Promise<SalesParkContract<object|null>>} - Return a SalesPark Contract object
 * History:
 * 01-05-2026: Created
 ******************************************************************/
export const getMovementsFromStoreByContext = async (context = {}) => {
  try {
    const movementsResponse = await getMovementsFromStore();
    if (movementsResponse.status === false) {
      return movementsResponse;
    }

    const records = movementsResponse.data || [];
    const filteredRecords = records.filter((record) => {
      const payload = record?.data || {};
      const userId = record?.user_id || payload?.user_id || payload?.queued_by?.user_id || null;
      const serviceId = record?.service_id_raw || payload?.service_id || payload?.service_id_raw || null;
      const equipmentId = record?.equipment || payload?.equipment || null;
      const movementDate = record?.date || payload?.date || null;

      if (context?.user_id && userId && String(userId || "") !== String(context.user_id)) {
        return false;
      }
      if (context?.service_id_raw && String(serviceId || "") !== String(context.service_id_raw)) {
        return false;
      }
      if (context?.equipment && String(equipmentId || "") !== String(context.equipment)) {
        return false;
      }
      if (context?.date && movementDate && isSameDay(movementDate, context.date) === false) {
        return false;
      }
      return true;
    });

    if (filteredRecords.length === 0) {
      return { status: true, data: null };
    }

    filteredRecords.sort((left, right) => {
      const leftDate = new Date(left?.updated_at || left?.queued_at || left?.date || 0).getTime();
      const rightDate = new Date(right?.updated_at || right?.queued_at || right?.date || 0).getTime();
      return rightDate - leftDate;
    });

    return { status: true, data: filteredRecords[0] };
  } catch (error) {
    console.error("getMovementsFromStoreByContext error:", error);
    return { status: false, data: error };
  }
};

/******************************************************************
 * ##: deleteMovementsFromStoreByContext - Remove movement drafts matching context
 * Deletes movement draft records from IndexedDB by matching user/service/equipment context.
 * @param {object} context - Context used to filter movement draft records
 * @returns {Promise<SalesParkContract<object>>} - Return a SalesPark Contract object
 * History:
 * 01-05-2026: Created
 ******************************************************************/
export const deleteMovementsFromStoreByContext = async (context = {}) => {
  try {
    const movementsResponse = await getMovementsFromStore();
    if (movementsResponse.status === false) {
      return movementsResponse;
    }

    const records = movementsResponse.data || [];
    const recordsToDelete = records.filter((record) => {
      const payload = record?.data || {};
      const userId = record?.user_id || payload?.user_id || payload?.queued_by?.user_id || null;
      const serviceId = record?.service_id_raw || payload?.service_id || payload?.service_id_raw || null;
      const equipmentId = record?.equipment || payload?.equipment || null;

      if (context?.user_id && userId && String(userId || "") !== String(context.user_id)) {
        return false;
      }
      if (context?.service_id_raw && String(serviceId || "") !== String(context.service_id_raw)) {
        return false;
      }
      if (context?.equipment && String(equipmentId || "") !== String(context.equipment)) {
        return false;
      }
      return true;
    });

    if (recordsToDelete.length === 0) {
      return { status: true, data: "OK" };
    }

    const db = await openDatabase();
    const transaction = db.transaction("movements", "readwrite");
    const store = transaction.objectStore("movements");
    recordsToDelete.forEach((record) => {
      if (record?.id) {
        store.delete(record.id);
      }
    });

    return await new Promise((resolve) => {
      transaction.oncomplete = () => {
        db.close();
        resolve({ status: true, data: "OK" });
      };
      transaction.onerror = () => {
        db.close();
        resolve({ status: false, data: transaction.error });
      };
    });
  } catch (error) {
    console.error("deleteMovementsFromStoreByContext error:", error);
    return { status: false, data: error };
  }
};

export const getSelectedServiceForStaff = async ({ staff_id } = {}) => {
  try {
    if (!staff_id) {
      return { status: false, data: "Staff ID is required" };
    }

    const db = await openDatabase();
    const transaction = db.transaction("selectedServiceByStaff", "readonly");
    const store = transaction.objectStore("selectedServiceByStaff");
    const request = store.get(String(staff_id));

    return new Promise((resolve) => {
      request.onsuccess = () => {
        const selectedService = request.result?.service || null;
        resolve({ status: true, data: selectedService });
        db.close();
      };
      request.onerror = () => {
        resolve({ status: false, data: request.error });
        db.close();
      };
    });
  } catch (error) {
    console.error("getSelectedServiceForStaff error:", error);
    return { status: false, data: error };
  }
};

export const setSelectedServiceForStaff = async ({ staff_id, service } = {}) => {
  try {
    if (!staff_id) {
      return { status: false, data: "Staff ID is required" };
    }
    if (!service || typeof service !== "object") {
      return { status: false, data: "Service is required" };
    }

    const db = await openDatabase();
    const transaction = db.transaction("selectedServiceByStaff", "readwrite");
    const store = transaction.objectStore("selectedServiceByStaff");

    store.put({
      id: String(staff_id),
      staff_id: String(staff_id),
      service_id_raw: service?.service_id_raw || null,
      embarked_count: service?.embarked_count ?? null,
      start_date_effective: service?.start_date_effective || null,
      end_date_effective: service?.end_date_effective || null,
      service,
      updated_at: new Date().toISOString(),
    });

    return await new Promise((resolve) => {
      transaction.oncomplete = () => {
        db.close();
        resolve({ status: true, data: "OK" });
      };
      transaction.onerror = () => {
        db.close();
        resolve({ status: false, data: transaction.error });
      };
    });
  } catch (error) {
    console.error("setSelectedServiceForStaff error:", error);
    return { status: false, data: error };
  }
};

export const clearSelectedServiceForStaff = async ({ staff_id } = {}) => {
  try {
    if (!staff_id) {
      return { status: false, data: "Staff ID is required" };
    }

    const db = await openDatabase();
    const transaction = db.transaction("selectedServiceByStaff", "readwrite");
    const store = transaction.objectStore("selectedServiceByStaff");
    store.delete(String(staff_id));

    return await new Promise((resolve) => {
      transaction.oncomplete = () => {
        db.close();
        resolve({ status: true, data: "OK" });
      };
      transaction.onerror = () => {
        db.close();
        resolve({ status: false, data: transaction.error });
      };
    });
  } catch (error) {
    console.error("clearSelectedServiceForStaff error:", error);
    return { status: false, data: error };
  }
};

/******************************************************************
 * ##: Read all cached staffs from IndexedDB for offline login
 * Loads the full list of synced staff records from the "/staff/list" object store so the
 * login screen can authenticate users locally when offline (email + boats_pin_code_hash).
 * @returns {Promise<SalesParkContract<object[]>>} - Return a SalesPark Contract object (with Promise).
 * History:
 * 29-04-2026: Created
 ******************************************************************/
export const getStaffsFromCache = async () => {
  try {
    if (typeof indexedDB === "undefined") {
      return { status: false, data: "IndexedDB not available" };
    }

    const db = await openDatabase();
    const transaction = db.transaction("/staff/list", "readonly");
    const store = transaction.objectStore("/staff/list");
    const request = store.getAll();

    return new Promise((resolve) => {
      request.onsuccess = () => {
        resolve({ status: true, data: request.result || [] });
        db.close();
      };

      request.onerror = () => {
        resolve({ status: false, data: request.error });
        db.close();
      };
    });

    // Error handling
  } catch (error) {
    console.error("getStaffsFromCache error:", error);
    return { status: false, data: error };
  }
};

const blobToDataUrl = (blob) => {
  return new Promise((resolve, reject) => {
    try {
      const reader = new FileReader();
      reader.onloadend = () => resolve(String(reader.result || ""));
      reader.onerror = () => reject(new Error("Error converting blob to base64 data URL"));
      reader.readAsDataURL(blob);
    } catch (error) {
      reject(error);
    }
  });
};

const fetchWithTimeout = async (url, timeoutMs) => {
  const controller = typeof AbortController !== "undefined" ? new AbortController() : null;
  const timeoutId = setTimeout(() => {
    if (controller) {
      controller.abort();
    }
  }, timeoutMs);

  try {
    if (controller) {
      return await fetch(url, { signal: controller.signal });
    }

    return await Promise.race([fetch(url), new Promise((_, reject) => setTimeout(() => reject(new Error("Fetch timed out")), timeoutMs))]);
  } finally {
    clearTimeout(timeoutId);
  }
};

const buildStaffPictureProxyUrl = (pictureUrl) => {
  try {
    if (!process.env.REACT_APP_API_URL) {
      return null;
    }
    const parsed = new URL(pictureUrl);
    const isSpacesHost = parsed.hostname.endsWith(".digitaloceanspaces.com");
    if (!isSpacesHost) {
      return null;
    }
    const match = parsed.pathname.match(/\/staff\/profiles\/([^/]+)$/i);
    const key = match?.[1] ? String(match[1]).trim() : "";
    if (!key) {
      return null;
    }
    return new URL(`/boats-mobile/staff/picture/${encodeURIComponent(key)}`, process.env.REACT_APP_API_URL).toString();
  } catch (error) {
    return null;
  }
};

const normalizePictureUrl = (picture) => {
  if (!picture) return null;
  const raw = String(picture).trim();
  if (!raw) return null;

  if (raw.startsWith("data:image/")) {
    return raw;
  }
  if (raw.startsWith("http://") || raw.startsWith("https://") || raw.startsWith("/")) {
    return raw;
  }

  return null;
};

/******************************************************************
 * ##: saveStaffPicturesToCache — Cache staff pictures in IndexedDB
 * Stores staff pictures in the local IndexedDB store `/assets/staff-pictures`
 * to support offline usage and reduce repeated image fetching.
 *
 * Notes:
 * - Accepts pictures as `data:image/*` (saved directly) or same-origin URLs (fetched and converted).
 * - Cross-origin URLs are intentionally skipped to avoid CORS and leaking requests to unknown hosts.
 * - Progress can be reported via `options.onProgress(message)`.
 *
 * TL;DR: Downloads/normalizes staff pictures and persists them to IndexedDB.
 * @param {Array<object>} staffs - Staff list, each item should contain `_id` or `id` and `picture`
 * @param {object} options - Optional configuration
 * @param {(message: string) => void} [options.onProgress] - Progress callback
 * @returns {Promise<SalesParkContract<object>>} - Return a SalesPark Contract object
 * History:
 * 30-04-2026: Added documentation block
 ******************************************************************/
export const saveStaffPicturesToCache = async (staffs, options = {}) => {
  try {
    if (typeof indexedDB === "undefined") {
      return { status: false, data: "IndexedDB not available" };
    }
    if (!Array.isArray(staffs)) {
      return { status: false, data: "Staff list is required" };
    }

    const reportProgress = (message) => {
      if (typeof options?.onProgress === "function") {
        options.onProgress(message);
      }
    };

    let saved = 0;
    let failed = 0;
    let skipped = 0;

    const recordsToSave = [];

    for (let i = 0; i < staffs.length; i += 1) {
      const staff = staffs[i];
      try {
        const staffId = staff?._id ? String(staff._id) : staff?.id ? String(staff.id) : null;
        if (!staffId) {
          continue;
        }

        const picture = normalizePictureUrl(staff?.picture);
        if (!picture) {
          // console.log("[offlineCache] staff picture skipped: missing/invalid picture", { staffId, picture: staff?.picture });
          continue;
        }

        reportProgress(`A guardar imagem do staff (${i + 1}/${staffs.length})...`);

        if (picture.startsWith("data:image/")) {
          recordsToSave.push({ staff_id: staffId, data_url: picture, updated_at: new Date().toISOString() });
          saved += 1;
          continue;
        }

        const candidateUrls = [];
        const allowedOrigins = new Set([window.location.origin]);
        try {
          if (process.env.REACT_APP_API_URL) {
            allowedOrigins.add(new URL(process.env.REACT_APP_API_URL).origin);
          }
        } catch (error) {
          // ignore invalid env var values
        }
        // Allow known public asset hosts (e.g. DigitalOcean Spaces) used by this app.
        allowedOrigins.add("https://magnificodouro.ams3.digitaloceanspaces.com");

        if (picture.startsWith("http://") || picture.startsWith("https://")) {
          const proxyUrl = buildStaffPictureProxyUrl(picture);
          if (proxyUrl) {
            candidateUrls.push(proxyUrl);
          }
          candidateUrls.push(picture);
        } else if (picture.startsWith("/")) {
          // Some pictures are hosted by the API (different origin) while others may be served by the app itself.
          // Try both to maximize cache hit rate.
          try {
            candidateUrls.push(new URL(picture, window.location.origin).toString());
          } catch (error) {
            // ignore
          }
          try {
            if (process.env.REACT_APP_API_URL) {
              candidateUrls.push(new URL(picture, process.env.REACT_APP_API_URL).toString());
            }
          } catch (error) {
            // ignore
          }
        }

        const uniqueCandidates = [...new Set(candidateUrls)].filter(Boolean);
        if (uniqueCandidates.length === 0) {
          // console.log("[offlineCache] staff picture failed: no candidate URLs", { staffId, picture });
          failed += 1;
          continue;
        }

        let imageResponse = null;
        let lastFetchError = null;
        let lastFetchStatus = null;
        let lastFetchUrl = null;
        const attempts = [];

        for (const urlCandidate of uniqueCandidates) {
          try {
            const parsed = new URL(urlCandidate);
            const isAllowedOrigin =
              allowedOrigins.has(parsed.origin) || parsed.hostname.endsWith(".digitaloceanspaces.com") || parsed.hostname.endsWith(".amazonaws.com");
            if (!isAllowedOrigin) continue;
            lastFetchUrl = urlCandidate;
            const response = await fetchWithTimeout(urlCandidate, 8000);
            lastFetchStatus = response?.status ?? null;
            attempts.push({ url: urlCandidate, status: lastFetchStatus, ok: Boolean(response?.ok) });
            if (response.ok) {
              imageResponse = response;
              break;
            }
          } catch (error) {
            lastFetchError = error;
            attempts.push({ url: urlCandidate, status: null, ok: false, error: String(error?.message || error) });
          }
        }

        if (!imageResponse) {
          // console.log("[offlineCache] staff picture fetch failed", {
          //   staffId,
          //   picture,
          //   candidates: uniqueCandidates,
          //   attempts,
          //   lastFetchUrl,
          //   lastFetchStatus,
          //   lastFetchError: lastFetchError ? String(lastFetchError?.message || lastFetchError) : null,
          // });
          skipped += 1;
          continue;
        }

        const blob = await imageResponse.blob();
        const dataUrl = await blobToDataUrl(blob);
        if (!dataUrl || !dataUrl.startsWith("data:image/")) {
          // console.log("[offlineCache] staff picture conversion failed", {
          //   staffId,
          //   picture,
          //   responseContentType: imageResponse.headers?.get?.("content-type") || null,
          // });
          failed += 1;
          continue;
        }

        recordsToSave.push({ staff_id: staffId, data_url: dataUrl, updated_at: new Date().toISOString() });
        saved += 1;
      } catch (error) {
        failed += 1;
      }
    }

    const db = await openDatabase();
    const transaction = db.transaction("/assets/staff-pictures", "readwrite");
    const store = transaction.objectStore("/assets/staff-pictures");

    const transactionResultPromise = new Promise((resolve) => {
      transaction.oncomplete = () => {
        db.close();
        try {
          if (typeof window !== "undefined" && typeof window.dispatchEvent === "function") {
            window.dispatchEvent(new CustomEvent("md-cache:staff-pictures-updated"));
          }
        } catch (error) {
          console.error("saveStaffPicturesToCache error:", error);
          // ignore event dispatch failures
        }
        resolve({ status: true, data: { saved, failed, skipped } });
      };
      transaction.onerror = () => {
        db.close();
        resolve({ status: false, data: transaction.error || "Error saving staff pictures" });
      };
    });

    for (const record of recordsToSave) {
      store.put(record);
    }

    return await transactionResultPromise;
  } catch (error) {
    console.error("saveStaffPicturesToCache error:", error);
    return { status: false, data: "Unexpected error saving staff pictures" };
  }
};

/******************************************************************
 * ##: getStaffPictureFromCache — Get cached staff picture data URL
 * Reads the staff picture record from `/assets/staff-pictures` and returns
 * its `data_url` (or `null` when not found).
 *
 * TL;DR: Retrieves a staff picture data URL from IndexedDB by staff id.
 * @param {string} staff_id - Staff id key used in the `/assets/staff-pictures` store
 * @returns {Promise<SalesParkContract<string|null>>} - Return a SalesPark Contract object
 * History:
 * 30-04-2026: Added documentation block
 ******************************************************************/
export const getStaffPictureFromCache = async (staff_id) => {
  try {
    if (typeof indexedDB === "undefined") {
      return { status: false, data: "IndexedDB not available" };
    }
    if (!staff_id) {
      return { status: false, data: "Staff ID is required" };
    }

    const db = await openDatabase();
    const transaction = db.transaction("/assets/staff-pictures", "readonly");
    const store = transaction.objectStore("/assets/staff-pictures");
    const request = store.get(String(staff_id));

    return await new Promise((resolve) => {
      request.onsuccess = () => {
        const dataUrl = request.result?.data_url || null;
        resolve({ status: true, data: dataUrl });
        db.close();
      };

      request.onerror = () => {
        resolve({ status: false, data: request.error });
        db.close();
      };
    });
  } catch (error) {
    console.error("getStaffPictureFromCache error:", error);
    return { status: false, data: "Unexpected error reading staff picture cache" };
  }
};

/******************************************************************
 * ##: getStoredNewStocks — Retrieve all pending stock updates from local store
 * Opens a readonly IndexedDB transaction on stocksToUpdate store
 * and retrieves all stored stock records pending synchronization.
 *
 * TL;DR: Fetches all locally stored stock updates from IndexedDB.
 * Used to sync pending stock changes with backend.
 * @returns {Promise<SalesParkContract<object>>} - Return a SalesPark Contract object (with Promise).
 * History:
 * 19-02-2026: Created
 ******************************************************************/
export const getStoredNewStocks = async () => {
  try {
    const db = await openDatabase();
    const transaction = db.transaction("stocksToUpdate", "readonly");
    const store = transaction.objectStore("stocksToUpdate");
    const request = store.getAll();

    return new Promise((resolve) => {
      request.onsuccess = () => {
        resolve({ status: true, data: request.result });
        db.close();
      };

      request.onerror = () => {
        resolve({ status: false, data: request.error });
        db.close();
      };
    });

    // Error handling
  } catch (error) {
    console.error("getStoredNewStocks error:", error);
    return { status: false, data: error };
  }
};

/******************************************************************
 * ##: getStoredNewStocksByContext — Retrieve pending stock updates by context
 * Reads pending stock updates from IndexedDB and filters by staff/service/equipment context.
 * @param {object} context - Context used to filter pending stocks
 * @returns {Promise<SalesParkContract<object[]>>} - Return a SalesPark Contract object
 * History:
 * 01-05-2026: Created
 ******************************************************************/
export const getStoredNewStocksByContext = async (context = {}) => {
  try {
    const stocksResponse = await getStoredNewStocks();
    if (stocksResponse.status === false) {
      return stocksResponse;
    }

    const records = stocksResponse.data || [];
    const filteredRecords = records.filter((record) => {
      if (context?.user_id && record?.user_id && String(record?.user_id || "") !== String(context.user_id)) {
        return false;
      }
      if (context?.service_id_raw && String(record?.service_id_raw || "") !== String(context.service_id_raw)) {
        return false;
      }
      if (context?.equipment && String(record?.equipment || "") !== String(context.equipment)) {
        return false;
      }
      return true;
    });

    return { status: true, data: filteredRecords };
  } catch (error) {
    console.error("getStoredNewStocksByContext error:", error);
    return { status: false, data: error };
  }
};

/******************************************************************
 * ##: Remove a queued offline action from the IndexedDB "actions" store
 * Delete a specific queued action by its id after it has been successfully synced to the API to prevent replays.
 *
 * TL;DR: Dequeue a synced action from IndexedDB.
 * Deletes by id and resolves a success or failure contract.
 * @param {string} id - Action id key to delete from the "actions" object store
 * @returns {Promise<SalesParkContract<object>>} - Return a SalesPark Contract object
 * History:
 * 12-02-2026: Updated
 ******************************************************************/
export const deleteActionFromStore = async (id) => {
  try {
    // Clear a specific action from IndexedDB by its key
    const db = await openDatabase();
    const transaction = db.transaction("actions", "readwrite");
    const store = transaction.objectStore("actions");

    return new Promise((resolve) => {
      store.delete(id);

      transaction.oncomplete = () => {
        db.close();
        resolve({ status: true, data: "OK" });
      };
      transaction.onerror = () => {
        db.close();
        resolve({ status: false, data: transaction.error });
      };
    });

    // Error handling
  } catch (error) {
    console.error("clearActionFromStore error:", error);
    return { status: false, data: error };
  }
};

/******************************************************************
 * ##: terminateService — Update a cached service record in IndexedDB
 * Updates a local service entry in IndexedDB by merging new payload data
 * with an existing record identified by service_id_raw and persists changes.
 *
 * TL;DR: Updates a locally cached service by merging payload into it.
 * Returns updated record or failure if not found or DB error occurs.
 * @param {string} serviceId - Raw service identifier used to match local record
 * @param {object} payload - Partial service object with fields to be updated
 * @returns {Promise<SalesParkContract<object>>} - Return a SalesPark Contract object (with Promise).
 * History:
 * 16-02-2026: Created
 ******************************************************************/
export const terminateService = async (serviceId, payload) => {
  if (isOnline()) return { status: true, data: "DB is online" };

  try {
    const db = await openDatabase();
    const transaction = db.transaction("/services", "readwrite");
    const store = transaction.objectStore("/services");
    const request = store.getAll();

    return new Promise((resolve) => {
      request.onsuccess = () => {
        const services = request.result;
        // Find the specific record where service_id_raw matches serviceId
        const serviceIndex = services.findIndex((s) => s.service_id_raw === serviceId);

        if (serviceIndex === -1) {
          db.close();
          resolve({ status: false, data: "Service not found in local cache" });
          return;
        }

        // Merge existing data with the new payload
        const updatedService = {
          ...services[serviceIndex],
          ...payload,
          id: services[serviceIndex].id, // Ensure the IndexedDB key remains unchanged
        };

        const updateRequest = store.put(updatedService);

        updateRequest.onsuccess = () => {
          db.close();
          resolve({ status: true, data: updatedService });
        };

        updateRequest.onerror = () => {
          db.close();
          resolve({ status: false, data: updateRequest.error });
        };
      };

      request.onerror = () => {
        db.close();
        resolve({ status: false, data: request.error });
      };
    });
  } catch (error) {
    console.error("terminateService error:", error);
    return { status: false, data: error };
  }
};

/******************************************************************
 * ##: updateMoneyBox — Increment or create total_sell_price in IndexedDB
 * Retrieves a sales record from IndexedDB by key and increments its
 * total_sell_price field, or creates a new record if it does not exist.
 *
 * TL;DR: Increments or creates a local money box total in IndexedDB.
 * Ensures total_sell_price is accumulated per key.
 * @param {string} key - Unique identifier used as primary key in store
 * @param {number} value - Amount to increment total_sell_price by
 * @returns {Promise<SalesParkContract<object>>} - Return a SalesPark Contract object (with Promise).
 * History:
 * 17-02-2026: Created
 ******************************************************************/
export const updateMoneyBox = async (key, value) => {
  try {
    const db = await openDatabase();
    const transaction = db.transaction("/goods/sales", "readwrite");
    const store = transaction.objectStore("/goods/sales");

    const request = store.get(key);
    return new Promise((resolve) => {
      request.onsuccess = () => {
        let moneyBox = request.result;

        if (!moneyBox) {
          moneyBox = {
            _id: key,
            id: key,
            total_sell_price: value,
          };
        } else {
          moneyBox.total_sell_price += value;
        }

        const updateRequest = store.put(moneyBox);

        updateRequest.onsuccess = () => {
          db.close();
          resolve({ status: true, data: moneyBox });
        };

        updateRequest.onerror = () => {
          db.close();
          resolve({ status: false, data: updateRequest.error });
        };
      };

      request.onerror = () => {
        db.close();
        resolve({ status: false, data: request.error });
      };
    });
  } catch (error) {
    console.error("updateMoneyBox error:", error);
    return { status: false, data: error };
  }
};

/******************************************************************
 * ##: clearStore — Remove all records from a given IndexedDB object store
 * Opens an IndexedDB transaction for the provided store name and clears
 * all persisted records inside that object store.
 *
 * TL;DR: Clears every record from a specified IndexedDB store.
 * Returns success or error wrapped in SalesParkContract format.
 * @param {string} storeName - Name of the IndexedDB object store to clear
 * @returns {Promise<SalesParkContract<object>>} - Return a SalesPark Contract object (with Promise).
 * History:
 * 18-02-2026: Created
 ******************************************************************/
export const clearStore = async (storeName) => {
  return new Promise(async (resolve) => {
    try {
      const db = await openDatabase();
      const transaction = db.transaction(storeName, "readwrite");
      const store = transaction.objectStore(storeName);
      const request = store.clear();
      request.onsuccess = () => {
        db.close();
        console.log("store cleared", storeName);
        resolve({ status: true, data: "OK" });
      };
      request.onerror = () => {
        db.close();
        resolve({ status: false, data: request.error });
      };
    } catch (error) {
      console.error("clearStore error:", error);
      return { status: false, data: error };
    }
  });
};

export const deleteDatabase = () => {
  return new Promise((resolve) => {
    const request = indexedDB.deleteDatabase(CACHE_PREFIX);

    request.onsuccess = () => {
      console.log("Database deleted");
      resolve({ status: true, data: "OK" });
    };

    request.onerror = () => {
      resolve({ status: false, data: request.error });
    };

    request.onblocked = () => {
      console.warn("Delete blocked (close other tabs)");
    };
  });
};

/******************************************************************
 * ##: Save a GPS location record into the IndexedDB "locationRecords" store
 * Persists a single location record (automatic or action-triggered) so it can be
 * synced later through the existing sync engine. The record id is the keyPath.
 *
 * TL;DR: Store one offline location record keyed by its client id.
 * @param {object} record - Location record (see utils/location/record.js buildLocationRecord)
 * @returns {Promise<SalesParkContract<object>>} - Return a SalesPark Contract object (with Promise).
 * History:
 * 23-07-2026: Created
 ******************************************************************/
export const saveLocationRecord = (record) => {
  return new Promise(async (resolve) => {
    try {
      if (!record || !record.id) {
        resolve({ status: false, data: "Location record id is required" });
        return;
      }

      const db = await openDatabase();
      const transaction = db.transaction("locationRecords", "readwrite");
      const store = transaction.objectStore("locationRecords");
      store.put(record);

      transaction.oncomplete = () => {
        db.close();
        resolve({ status: true, data: record });
      };
      transaction.onerror = () => {
        db.close();
        resolve({ status: false, data: transaction.error });
      };
    } catch (error) {
      console.error("saveLocationRecord error:", error);
      resolve({ status: false, data: error });
    }
  });
};

/******************************************************************
 * ##: Read all location records for a given service from IndexedDB
 * Returns every stored location record whose service_id_raw matches, sorted by
 * recorded_at ascending (chronological route order).
 *
 * TL;DR: Get a service's stored positions in chronological order.
 * @param {string} serviceIdRaw - Active service/trip business id
 * @returns {Promise<SalesParkContract<object[]>>} - Return a SalesPark Contract object (with Promise).
 * History:
 * 23-07-2026: Created
 ******************************************************************/
export const getLocationRecordsByService = async (serviceIdRaw) => {
  try {
    const db = await openDatabase();
    const transaction = db.transaction("locationRecords", "readonly");
    const store = transaction.objectStore("locationRecords");
    const request = store.getAll();

    return new Promise((resolve) => {
      request.onsuccess = () => {
        const records = (request.result || [])
          .filter((record) => String(record?.service_id_raw || "") === String(serviceIdRaw || ""))
          .sort((left, right) => new Date(left.recorded_at).getTime() - new Date(right.recorded_at).getTime());
        resolve({ status: true, data: records });
        db.close();
      };
      request.onerror = () => {
        resolve({ status: false, data: request.error });
        db.close();
      };
    });
  } catch (error) {
    console.error("getLocationRecordsByService error:", error);
    return { status: false, data: error };
  }
};

/******************************************************************
 * ##: Read the last stored location record for a service
 * Returns the most recent (by recorded_at) location record for the given service,
 * used to evaluate the 20m / 60s persistence rule after a reload.
 *
 * TL;DR: Get a service's latest stored position (or null).
 * @param {string} serviceIdRaw - Active service/trip business id
 * @returns {Promise<SalesParkContract<object|null>>} - Return a SalesPark Contract object (with Promise).
 * History:
 * 23-07-2026: Created
 ******************************************************************/
export const getLastLocationRecordForService = async (serviceIdRaw) => {
  try {
    const recordsResponse = await getLocationRecordsByService(serviceIdRaw);
    if (recordsResponse.status === false) {
      return recordsResponse;
    }
    const records = recordsResponse.data || [];
    const last = records.length > 0 ? records[records.length - 1] : null;
    return { status: true, data: last };
  } catch (error) {
    console.error("getLastLocationRecordForService error:", error);
    return { status: false, data: error };
  }
};

/******************************************************************
 * ##: Read all unsynced location records from IndexedDB
 * Returns every location record whose synced_at is still null, sorted by recorded_at
 * ascending, so the sync engine can push them in chronological order.
 *
 * TL;DR: Get location records pending synchronization.
 * @returns {Promise<SalesParkContract<object[]>>} - Return a SalesPark Contract object (with Promise).
 * History:
 * 23-07-2026: Created
 ******************************************************************/
export const getUnsyncedLocationRecords = async () => {
  try {
    const db = await openDatabase();
    const transaction = db.transaction("locationRecords", "readonly");
    const store = transaction.objectStore("locationRecords");
    const request = store.getAll();

    return new Promise((resolve) => {
      request.onsuccess = () => {
        const records = (request.result || [])
          .filter((record) => record?.synced_at === null || record?.synced_at === undefined)
          .sort((left, right) => new Date(left.recorded_at).getTime() - new Date(right.recorded_at).getTime());
        resolve({ status: true, data: records });
        db.close();
      };
      request.onerror = () => {
        resolve({ status: false, data: request.error });
        db.close();
      };
    });
  } catch (error) {
    console.error("getUnsyncedLocationRecords error:", error);
    return { status: false, data: error };
  }
};

/******************************************************************
 * ##: Mark location records as synced
 * Stamps synced_at (ISO now) on the records whose id is in the provided list,
 * only after the server has confirmed them. Records not found are ignored.
 *
 * TL;DR: Confirm sync locally by setting synced_at on the given ids.
 * @param {string[]} ids - Confirmed location record ids returned by the server
 * @returns {Promise<SalesParkContract<object>>} - Return a SalesPark Contract object (with Promise).
 * History:
 * 23-07-2026: Created
 ******************************************************************/
export const markLocationRecordsSynced = (ids = []) => {
  return new Promise(async (resolve) => {
    try {
      if (!Array.isArray(ids) || ids.length === 0) {
        resolve({ status: true, data: { updated: 0 } });
        return;
      }

      const idSet = new Set(ids.map((id) => String(id)));
      const syncedAt = new Date().toISOString();

      const db = await openDatabase();
      const transaction = db.transaction("locationRecords", "readwrite");
      const store = transaction.objectStore("locationRecords");
      const getAllRequest = store.getAll();
      let updated = 0;

      getAllRequest.onsuccess = () => {
        const records = getAllRequest.result || [];
        records.forEach((record) => {
          if (record && idSet.has(String(record.id)) && !record.synced_at) {
            store.put({ ...record, synced_at: syncedAt });
            updated += 1;
          }
        });
      };

      transaction.oncomplete = () => {
        db.close();
        resolve({ status: true, data: { updated } });
      };
      transaction.onerror = () => {
        db.close();
        resolve({ status: false, data: transaction.error });
      };
    } catch (error) {
      console.error("markLocationRecordsSynced error:", error);
      resolve({ status: false, data: error });
    }
  });
};

/******************************************************************
 * ##: Prune synced location records for ended services
 * Removes location records that are already synced (synced_at set) and do NOT belong
 * to the currently active service, keeping IndexedDB from growing unbounded while
 * preserving the active service's history (needed for the 20m/60s rule after reload).
 *
 * TL;DR: Delete synced positions from services other than the active one.
 * @param {string|null} activeServiceIdRaw - service_id_raw to keep (or null to keep none active)
 * @returns {Promise<SalesParkContract<object>>} - Return a SalesPark Contract object (with Promise).
 * History:
 * 23-07-2026: Created
 ******************************************************************/
export const pruneSyncedLocationRecords = (activeServiceIdRaw = null) => {
  return new Promise(async (resolve) => {
    try {
      const db = await openDatabase();
      const transaction = db.transaction("locationRecords", "readwrite");
      const store = transaction.objectStore("locationRecords");
      const getAllRequest = store.getAll();
      let removed = 0;

      getAllRequest.onsuccess = () => {
        const records = getAllRequest.result || [];
        records.forEach((record) => {
          const isSynced = Boolean(record?.synced_at);
          const isActiveService = String(record?.service_id_raw || "") === String(activeServiceIdRaw || "");
          if (isSynced && !isActiveService) {
            store.delete(record.id);
            removed += 1;
          }
        });
      };

      transaction.oncomplete = () => {
        db.close();
        resolve({ status: true, data: { removed } });
      };
      transaction.onerror = () => {
        db.close();
        resolve({ status: false, data: transaction.error });
      };
    } catch (error) {
      console.error("pruneSyncedLocationRecords error:", error);
      resolve({ status: false, data: error });
    }
  });
};

/******************************************************************
 * ##: createNewStocksInStore — Insert or update stock entries in local store
 * Compares incoming stock items with existing records in the stocksToUpdate
 * store and updates quantity if found, otherwise inserts a new entry.
 *
 * TL;DR: Syncs incoming stock list with local stocksToUpdate store.
 * Updates existing quantities or inserts new stock records.
 * @param {Array<object>} stocks - List of stock objects containing wares, equipment and quantity
 * @returns {Promise<SalesParkContract<object>>} - Return a SalesPark Contract object (with Promise).
 * History:
 * 19-02-2026: Created
 ******************************************************************/
export const createNewStocksInStore = (stocks) => {
  return new Promise(async (resolve) => {
    try {
      const db = await openDatabase();
      const transaction = db.transaction("stocksToUpdate", "readwrite");
      const store = transaction.objectStore("stocksToUpdate");

      const getAllRequest = store.getAll();

      getAllRequest.onsuccess = () => {
        const currentStocks = getAllRequest.result;
        const updatedResults = [];

        stocks.forEach((incomingItem) => {
          const existingRecord = currentStocks.find((s) => {
            return (
              s.wares === incomingItem.wares &&
              s.equipment === incomingItem.equipment &&
              String(s.user_id || "") === String(incomingItem.user_id || "") &&
              String(s.service_id_raw || "") === String(incomingItem.service_id_raw || "")
            );
          });
          if (existingRecord) {
            const updatedRecord = {
              ...existingRecord,
              quantity: incomingItem.quantity,
            };

            store.put(updatedRecord);
            updatedResults.push(updatedRecord);
          } else {
            const key = `${incomingItem.wares}_${incomingItem.equipment}_${incomingItem.user_id || "nouser"}_${incomingItem.service_id_raw || "noservice"}`;
            store.put({
              ...incomingItem,
              id: key,
            });
          }
        });

        transaction.oncomplete = () => {
          db.close();
          resolve({ status: true, data: updatedResults });
        };

        transaction.onerror = () => {
          db.close();
          resolve({ status: false, data: "Transaction failed" });
        };
      };

      getAllRequest.onerror = () => {
        db.close();
        resolve({ status: false, data: getAllRequest.error });
      };
    } catch (error) {
      console.error("updateStocksStore error:", error);
      resolve({ status: false, data: error });
    }
  });
};

/******************************************************************
 * ##: createNewStocksInStoreByContext — Insert/update stock entries with context metadata
 * Persists stock updates in local store and tags each item with user/service metadata for safe multi-staff isolation.
 * @param {Array<object>} stocks - List of stock objects containing wares, equipment and quantity
 * @param {object} context - Context metadata (user_id, service_id_raw)
 * @returns {Promise<SalesParkContract<object>>} - Return a SalesPark Contract object
 * History:
 * 01-05-2026: Created
 ******************************************************************/
export const createNewStocksInStoreByContext = (stocks, context = {}) => {
  const scopedStocks = (stocks || []).map((stock) => ({
    ...stock,
    user_id: context?.user_id || null,
    service_id_raw: context?.service_id_raw || null,
  }));

  return createNewStocksInStore(scopedStocks);
};

/******************************************************************
 * ##: updateStocksStore — Update existing stock quantities in IndexedDB
 * Updates quantities of existing stock records in /goods/stocks store
 * by matching wares._id and equipment._id against incoming items.
 *
 * TL;DR: Updates quantities of existing stock records in local store.
 * Skips creation and only modifies already persisted entries.
 * @param {Array<object>} stocks - Array containing wares, equipment and quantity values
 * @returns {Promise<SalesParkContract<object>>} - Return a SalesPark Contract object (with Promise).
 * History:
 * 19-02-2026: Created
 ******************************************************************/
export const updateStocksStore = (stocks) => {
  if (isOnline()) return { status: true, data: "DB is online" };

  return new Promise(async (resolve) => {
    try {
      const db = await openDatabase();
      const transaction = db.transaction("/goods/stocks", "readwrite");
      const store = transaction.objectStore("/goods/stocks");

      const getAllRequest = store.getAll();

      getAllRequest.onsuccess = () => {
        const currentStocks = getAllRequest.result;
        const updatedResults = [];

        stocks.forEach((incomingItem) => {
          const existingRecord = currentStocks.find((s) => s.wares._id === incomingItem.wares && s.equipment._id === incomingItem.equipment);

          if (existingRecord) {
            const updatedRecord = {
              ...existingRecord,
              quantity: incomingItem.quantity,
            };

            store.put(updatedRecord);
            updatedResults.push(updatedRecord);
          }
        });

        transaction.oncomplete = () => {
          db.close();
          resolve({ status: true, data: updatedResults });
        };

        transaction.onerror = () => {
          db.close();
          resolve({ status: false, data: "Transaction failed" });
        };
      };

      getAllRequest.onerror = () => {
        db.close();
        resolve({ status: false, data: getAllRequest.error });
      };
    } catch (error) {
      console.error("updateStocksStore error:", error);
      resolve({ status: false, data: error });
    }
  });
};

export const updateCountedQuantity = async (payload) => {
  return new Promise(async (resolve) => {
    try {
      const db = await openDatabase();
      const transaction = db.transaction("/goods/stocks", "readwrite");
      const store = transaction.objectStore("/goods/stocks");

      const getAllRequest = store.getAll();

      getAllRequest.onsuccess = () => {
        const currentStocks = getAllRequest.result;
        const updatedResults = [];
        payload.stocks.forEach((incomingItem) => {
          if (incomingItem.isNew === false) {
            const existingRecord = currentStocks.find((s) => s.wares._id === incomingItem.wares._id && s.equipment._id === payload.equipment._id);
            if (existingRecord) {
              const updatedRecord = {
                ...existingRecord,
                counted_quantity: incomingItem.counted_quantity,
              };
              store.put(updatedRecord);

              updatedResults.push(updatedRecord);
            }
          }
          if (incomingItem.isNew === true) {
            const key = `${incomingItem.wares._id}_${payload.equipment._id}`;
            const newRecord = {
              ...incomingItem,
              equipment: payload.equipment,
              id: key,
            };
            store.add(newRecord);
            updatedResults.push(newRecord);
          }
        });

        transaction.oncomplete = () => {
          db.close();
          resolve({ status: true, data: updatedResults });
        };

        transaction.onerror = (e) => {
          console.error("TX ERROR:", e.target.error);
          db.close();
          resolve({ status: false, data: "Transaction failed" });
        };
      };

      getAllRequest.onerror = (e) => {
        console.error("PUT ERROR:", e.target.error);
        db.close();
        resolve({ status: false, data: getAllRequest.error });
      };
    } catch (error) {
      console.error("updateStocksStore error:", error);
      resolve({ status: false, data: error });
    }
  });
};

export const createCountedStocksPayloadInStore = (payload) => {
  return new Promise(async (resolve) => {
    try {
      const db = await openDatabase();

      const transaction = db.transaction("countedStocks", "readwrite");
      const store = transaction.objectStore("countedStocks");

      const request = store.put(payload);

      request.onsuccess = () => {
        console.log("request.result", request.result);
      };

      request.onerror = (e) => {
        console.error("PUT ERROR:", e.target.error);
        db.close();
        resolve({ status: false, data: e.target.error });
      };

      transaction.oncomplete = () => {
        db.close();
        resolve({ status: true, data: payload });
      };

      transaction.onerror = (e) => {
        console.error("TX ERROR:", e.target.error);
        db.close();
        resolve({ status: false, data: e.target.error });
      };
    } catch (error) {
      console.error("createCountedStocksPayloadInStore error:", error);
      resolve({ status: false, data: error });
    }
  });
};

/******************************************************************
 * ##: getCountedStocksFromStoreByContext — Retrieve counted stocks payloads by context
 * Reads counted stock payloads from IndexedDB and filters by user/service/equipment context.
 * @param {object} context - Context used to filter counted stock payloads
 * @returns {Promise<SalesParkContract<object[]>>} - Return a SalesPark Contract object
 * History:
 * 01-05-2026: Created
 ******************************************************************/
export const getCountedStocksFromStoreByContext = async (context = {}) => {
  try {
    const db = await openDatabase();
    const transaction = db.transaction("countedStocks", "readonly");
    const store = transaction.objectStore("countedStocks");
    const request = store.getAll();

    return await new Promise((resolve) => {
      request.onsuccess = () => {
        const records = request.result || [];
        const filteredRecords = records.filter((record) => {
          if (context?.user_id && record?.user_id && String(record?.user_id || "") !== String(context.user_id)) {
            return false;
          }
          if (context?.service_id_raw && String(record?.service_id_raw || "") !== String(context.service_id_raw)) {
            return false;
          }
          if (context?.equipment && String(record?.equipment || "") !== String(context.equipment)) {
            return false;
          }
          return true;
        });
        resolve({ status: true, data: filteredRecords });
        db.close();
      };

      request.onerror = () => {
        resolve({ status: false, data: request.error });
        db.close();
      };
    });
  } catch (error) {
    console.error("getCountedStocksFromStoreByContext error:", error);
    return { status: false, data: error };
  }
};

/******************************************************************
 * ##: deleteCountedStocksFromStoreByContext — Remove counted stocks payloads by context
 * Deletes counted stock payloads from IndexedDB matching the provided user/service/equipment context.
 * @param {object} context - Context used to filter counted stock payloads
 * @returns {Promise<SalesParkContract<object>>} - Return a SalesPark Contract object
 * History:
 * 01-05-2026: Created
 ******************************************************************/
export const deleteCountedStocksFromStoreByContext = async (context = {}) => {
  try {
    const countedStocksResponse = await getCountedStocksFromStoreByContext(context);
    if (countedStocksResponse.status === false) {
      return countedStocksResponse;
    }

    const records = countedStocksResponse.data || [];
    if (records.length === 0) {
      return { status: true, data: "OK" };
    }

    const db = await openDatabase();
    const transaction = db.transaction("countedStocks", "readwrite");
    const store = transaction.objectStore("countedStocks");

    records.forEach((record) => {
      if (record?.id) {
        store.delete(record.id);
      }
    });

    return await new Promise((resolve) => {
      transaction.oncomplete = () => {
        db.close();
        resolve({ status: true, data: "OK" });
      };
      transaction.onerror = () => {
        db.close();
        resolve({ status: false, data: transaction.error });
      };
    });
  } catch (error) {
    console.error("deleteCountedStocksFromStoreByContext error:", error);
    return { status: false, data: error };
  }
};
